import React from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import BrandLogo from "@/components/BrandLogo";

function Brand({ size = "default" }) {
  return <BrandLogo size={size === "sm" ? "sm" : "md"} />;
}

export default function PublicLayout() {
  const { user } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  const links = [
    { to: "/", label: t("home") },
    { to: "/about", label: t("about_us") },
    { to: "/faq", label: t("faq") },
    { to: "/contact", label: t("contact") },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg)] overflow-x-hidden">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/" data-testid="brand-logo" className="shrink-0">
            <Brand size="sm" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  loc.pathname === l.to ? "bg-[var(--ink)] text-white" : "text-zinc-700 hover:text-[var(--ink)]"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <LanguageToggle />
            {user ? (
              <button
                data-testid="nav-dashboard-btn"
                onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[var(--ink)] text-white text-xs sm:text-sm font-semibold hover:bg-[var(--accent)] transition-colors"
              >
                {t("dashboard")} <span>→</span>
              </button>
            ) : (
              <Link to="/login" data-testid="nav-login-btn"
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[var(--ink)] text-white text-xs sm:text-sm font-semibold hover:bg-[var(--accent)] transition-colors">
                {t("login")} <span>→</span>
              </Link>
            )}
            <button data-testid="mobile-menu-btn" className="md:hidden p-1.5 rounded-md hover:bg-zinc-100" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-[var(--line)] bg-white">
            <div className="px-4 py-2 flex flex-col">
              {links.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2.5 text-sm font-medium">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="bg-[var(--ink)] text-zinc-200 mt-16 sm:mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14 grid md:grid-cols-4 gap-8 sm:gap-10">
          <div>
            <BrandLogo size="lg" dark />
            <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
              Forward-thinking technology platform for remote data work, typing certifications, and smart workforce management.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Quick Links</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-[var(--accent)]">{t("about")}</Link></li>
              <li><Link to="/faq" className="hover:text-[var(--accent)]">{t("faq")}</Link></li>
              <li><Link to="/contact" className="hover:text-[var(--accent)]">{t("contact")}</Link></li>
              <li><Link to="/login" className="hover:text-[var(--accent)]">{t("login")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">{t("contact")}</div>
            <ul className="space-y-2 text-sm text-zinc-300">
              <li>hr@pelicodata.co.in</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">© {new Date().getFullYear()}</div>
            <p className="text-xs text-zinc-400 leading-relaxed">Pelico Data. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
